import { DatePicker, useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { StateSelectField } from "@tellescope/forms";
import { useTranslation } from "react-i18next";
import { EditableField } from "./EditableField";
import { EditableTextField } from "./EditableTextField";
import { CustomFieldEditor, CustomFieldValue } from "./CustomFieldEditor";
import { FieldDescriptor } from "../../../../../hooks";

export interface FieldCellProps {
  field: FieldDescriptor;
  editing: boolean;
  canEdit: boolean;
  error?: string;
  currentUserId?: string;
  onStartEditing: (key: string) => void;
  onStopEditing: () => void;
  onSaveField: (key: string, update: Record<string, unknown>, validationValue?: string) => void;
  onSaveCustomField: (field: string, value: CustomFieldValue) => void;
}

export const FieldCell = ({
  field,
  editing,
  canEdit,
  error,
  currentUserId,
  onStartEditing,
  onStopEditing,
  onSaveField,
  onSaveCustomField,
}: FieldCellProps) => {
  const { t } = useTranslation("contacts");
  const { format } = useDateFnsFormattersWithLocale();
  const notProvided = t("Not provided");

  if (field.editorKind === "text") {
    return (
      <EditableTextField
        id={field.key}
        label={field.name}
        required={field.required}
        editing={editing}
        canEdit={canEdit}
        value={field.value ?? undefined}
        placeholder={notProvided}
        error={error}
        onStartEditing={() => onStartEditing(field.key)}
        onSave={(next) =>
          field.key === "zipCode"
            ? onSaveField(
                "zipCode",
                (([zipCode, zipPlusFour]) => ({ zipCode, zipPlusFour: zipPlusFour || "" }))(next.split("-")),
                next
              )
            : onSaveField(field.key, { [field.key]: next }, next)
        }
        onCancel={onStopEditing}
      />
    );
  }

  if (field.editorKind === "date") {
    return (
      <EditableField
        id={field.key}
        label={field.name}
        required={field.required}
        editing={editing}
        canEdit={canEdit}
        onStartEditing={() => onStartEditing(field.key)}
        displayValue={field.value ? format(new Date(field.value), "MM/dd/yyyy") : undefined}
        placeholder={notProvided}
      >
        <DatePicker
          id={field.key}
          value={field.value ? new Date(field.value) : undefined}
          onChange={(d) => onSaveField(field.key, { [field.key]: d ? format(d, "MM-dd-yyyy") : "" })}
          className="space-y-0"
          label=""
        />
      </EditableField>
    );
  }

  if (field.editorKind === "state") {
    return (
      <EditableField
        id={field.key}
        label={field.name}
        required={field.required}
        editing={editing}
        canEdit={canEdit}
        onStartEditing={() => onStartEditing(field.key)}
        displayValue={field.value ?? undefined}
        placeholder={notProvided}
      >
        <StateSelectField
          id={field.key}
          value={field.value ?? undefined}
          onValueChange={(value) => onSaveField(field.key, { state: value })}
          className="flex-1"
        />
      </EditableField>
    );
  }

  if (field.editorKind === "custom") {
    return (
      <EditableField
        id={field.key}
        label={field.name}
        required={field.required}
        editing={editing}
        canEdit={canEdit}
        onStartEditing={() => onStartEditing(field.key)}
        displayValue={field.displayValue}
        placeholder={notProvided}
      >
        <CustomFieldEditor
          id={field.key}
          type={field.customType ?? ""}
          options={field.customOptions}
          value={field.rawValue}
          onChange={(next) => onSaveCustomField(field.name, next)}
          onCancel={onStopEditing}
          userId={currentUserId}
        />
      </EditableField>
    );
  }

  return (
    <EditableField
      id={field.key}
      label={field.name}
      required={field.required}
      editing={false}
      canEdit={false}
      onStartEditing={() => {}}
      displayValue={field.value ?? undefined}
      placeholder={notProvided}
    >
      {null}
    </EditableField>
  );
};
